import React, { Component, ErrorInfo, ReactNode } from 'react';
import { Card } from 'primereact/card';
import { Button } from 'primereact/button';
import './ErrorBoundary.css';

interface Props {
  children: ReactNode;
  fallback?: ReactNode;
  onError?: (error: Error, errorInfo: ErrorInfo) => void;
}

interface State {
  hasError: boolean;
  error: Error | null;
  errorInfo: ErrorInfo | null;
}

export class ErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = {
      hasError: false,
      error: null,
      errorInfo: null
    };
  }

  static getDerivedStateFromError(error: Error): State {
    return {
      hasError: true,
      error,
      errorInfo: null
    };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    // Log error to error reporting service
    console.error('ErrorBoundary caught an error:', error, errorInfo);
    
    // Call custom error handler if provided
    if (this.props.onError) {
      this.props.onError(error, errorInfo);
    }

    // Update state with error info
    this.setState({
      error,
      errorInfo
    });

    // In production, send to error tracking service
    if (process.env.NODE_ENV === 'production') {
      // Example: Sentry, LogRocket, etc.
      // logErrorToService(error, errorInfo);
    }
  }

  handleReset = () => {
    this.setState({
      hasError: false,
      error: null,
      errorInfo: null
    });
  };

  render() {
    if (this.state.hasError) {
      // Use custom fallback if provided
      if (this.props.fallback) {
        return <>{this.props.fallback}</>;
      }

      // Default error UI
      return (
        <div className="error-boundary-container">
          <Card className="error-card">
            <div className="error-icon">
              <i className="pi pi-exclamation-triangle"></i>
            </div>
            <h2>Oops! Something went wrong</h2>
            <p className="error-message">
              We're sorry, but something unexpected happened. Please try refreshing the page or contact support if the problem persists.
            </p>
            
            {process.env.NODE_ENV === 'development' && this.state.error && (
              <div className="error-details">
                <h3>Error Details (Development Only)</h3>
                <pre className="error-stack">
                  {this.state.error.toString()}
                  {this.state.errorInfo && this.state.errorInfo.componentStack}
                </pre>
              </div>
            )}

            <div className="error-actions">
              <Button
                label="Refresh Page"
                icon="pi pi-refresh"
                onClick={() => window.location.reload()}
                className="p-button-primary"
              />
              <Button
                label="Go to Dashboard"
                icon="pi pi-home"
                onClick={() => window.location.href = '/agent/dashboard'}
                className="p-button-secondary"
              />
              <Button
                label="Try Again"
                icon="pi pi-replay"
                onClick={this.handleReset}
                className="p-button-outlined"
              />
            </div>
          </Card>
        </div>
      );
    }

    return this.props.children;
  }
}

// Specific error boundary for dashboard sections
export class DashboardErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = {
      hasError: false,
      error: null,
      errorInfo: null
    };
  }

  static getDerivedStateFromError(error: Error): State {
    return {
      hasError: true,
      error,
      errorInfo: null
    };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    console.error('Dashboard error:', error, errorInfo);
  }

  render() {
    if (this.state.hasError) {
      return (
        <Card className="dashboard-error-card">
          <div className="dashboard-error-content">
            <i className="pi pi-exclamation-circle"></i>
            <p>Unable to load this section. Please try refreshing.</p>
            <Button
              label="Retry"
              icon="pi pi-refresh"
              size="small"
              onClick={() => this.setState({ hasError: false, error: null, errorInfo: null })}
            />
          </div>
        </Card>
      );
    }

    return this.props.children;
  }
}

// Table-specific error boundary
export class TableErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = {
      hasError: false,
      error: null,
      errorInfo: null
    };
  }

  static getDerivedStateFromError(error: Error): State {
    return {
      hasError: true,
      error,
      errorInfo: null
    };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    console.error('Table error:', error, errorInfo);
  }

  render() {
    if (this.state.hasError) {
      return (
        <div className="table-error-container">
          <div className="table-error-content">
            <i className="pi pi-database"></i>
            <h3>Unable to load data</h3>
            <p>There was a problem loading the table data. Please try again.</p>
            <Button
              label="Reload Data"
              icon="pi pi-refresh"
              onClick={() => window.location.reload()}
            />
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}

// Form-specific error boundary
export class FormErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = {
      hasError: false,
      error: null,
      errorInfo: null
    };
  }

  static getDerivedStateFromError(error: Error): State {
    return {
      hasError: true,
      error,
      errorInfo: null
    };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    console.error('Form error:', error, errorInfo);
  }

  render() {
    if (this.state.hasError) {
      return (
        <Card className="form-error-card">
          <div className="form-error-content">
            <i className="pi pi-exclamation-triangle"></i>
            <h3>Form Error</h3>
            <p>There was a problem with the form. Please refresh and try again.</p>
            <p className="error-hint">Your data has not been saved.</p>
            <Button
              label="Refresh Form"
              icon="pi pi-refresh"
              onClick={() => window.location.reload()}
            />
          </div>
        </Card>
      );
    }

    return this.props.children;
  }
}